import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  apiV2EnergyAnalysisUnitConfGroupSavePost,
  apiV2EnergyUnitBoardRecomputePost,
  V2EnergyConsumptionEvaluationAnalysisUnitUnitConfGroupListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Steps, Form, DatePicker, Modal, Button, Radio, TreeSelect, Tag } from '@maxtropy/components';
import { Space } from 'antd';
import styles from './index.module.scss';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { isNil } from 'lodash-es';
import check from '../assets/check.png';
export enum StepsType {
  First = 0,
  Second,
  Third,
}

export type GroupItem = Exclude<
  V2EnergyConsumptionEvaluationAnalysisUnitUnitConfGroupListPostResponse['list'],
  undefined
>[number];

type EffectiveDateModalProps = {
  // 分析组id
  unitId: string;
  closeModal: () => void;
  changedUnitConfGroupListDate?: string;
  refresh: () => void;
  setUnitConfGroupDetailActiveTab: React.Dispatch<React.SetStateAction<string>>;
  groupList: GroupItem[];
};

type FormProps = {
  effectiveDate: Dayjs;
  energyUnitIds: any[];
  radio: number;
};

interface EnergyNode {
  key?: number;
  value?: number;
  name?: string;
  children?: GroupItem[];
}

// 递归收集所有子节点 id
const getDescendantIds = (node: GroupItem): number[] => {
  if (!node?.childList || node.childList.length === 0) return [];
  const res: number[] = [];
  for (const child of node.childList) {
    res.push(child.id!);
    res.push(...getDescendantIds(child));
  }
  return res;
};

/** 将后端结构转换为 antd TreeSelect 的 treeData，title 中注入全选按钮 */
const transformToTreeData = (
  list: GroupItem[],
  onSelectAll: (node: GroupItem, select: boolean) => void,
  checkedValues: number[]
): EnergyNode[] =>
  list.map(item => {
    const descendantIds = getDescendantIds(item);
    const hasDescendants = descendantIds.length > 0;

    const isAllSelected = hasDescendants && descendantIds.every(id => checkedValues?.includes(id));

    return {
      title: (
        <Space size={6}>
          <span>{item.name}</span>
          {hasDescendants && (
            <Tag
              border="solid"
              color={isAllSelected ? 'info' : 'default'}
              onClick={e => {
                e.stopPropagation(); // 阻止触发节点的选中/展开行为
                e.preventDefault();
                onSelectAll(item, !isAllSelected);
              }}
            >
              全选子节点
            </Tag>
          )}
        </Space>
      ),
      value: item.id,
      name: item.name,
      key: item.id,
      children:
        item.childList && item.childList.length > 0
          ? transformToTreeData(item.childList, onSelectAll, checkedValues)
          : undefined,
    };
  });

// 生效日期选择
export default function EffectiveDateModal(props: EffectiveDateModalProps) {
  const { closeModal, unitId, changedUnitConfGroupListDate, refresh, setUnitConfGroupDetailActiveTab, groupList } =
    props;
  const [form] = Form.useForm<FormProps>();
  const [current, setCurrent] = useState<number>(0);
  const [effectiveDate, setEffectiveDate] = useState<Dayjs>(dayjs());
  const [energyUnitIds, setEnergyUnitIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState<number[]>();
  const energyUnitId = Form.useWatch('energyUnitIds', form);

  useEffect(() => {
    // 全选按钮返回值并没有传labelInValue 的值，所以需要判断
    if (current === 1) {
      if (energyUnitId && energyUnitId?.[0]?.value) {
        const mapData = energyUnitId.map((i: { value: number }) => Number(i.value));
        setEnergyUnitIds(mapData);
      } else {
        setEnergyUnitIds(energyUnitId.map(i => i));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energyUnitId]);

  useMemo(() => {
    const keys: number[] = [];
    groupList.forEach(node => {
      keys.push(node.id!);
      node.childList?.forEach(child => keys.push(child.id!));
    });
    setExpandedKeys(keys);
  }, [groupList]);

  // 批量选中/取消选中子节点
  const handleSelectAll = (node: GroupItem, select: boolean) => {
    const allDesc = getDescendantIds(node);
    if (allDesc.length === 0) return;
    const prevSet = new Set(energyUnitIds.map(v => Number(v)));
    if (select) {
      allDesc.forEach(id => prevSet.add(id));
    } else {
      allDesc.forEach(id => prevSet.delete(id));
    }
    const newEnergyUnitIds = Array.from(prevSet);
    setEnergyUnitIds(newEnergyUnitIds);
    form.setFieldValue('energyUnitIds', newEnergyUnitIds);
  };

  // 动态渲染带全选按钮的节点
  const treeData = useMemo(
    () => transformToTreeData(groupList, handleSelectAll, energyUnitIds),
    [groupList, energyUnitIds]
  );

  const handleExpand = (keys: React.Key[]) => setExpandedKeys(keys as number[]);

  const onFinish = () => {
    if (isNil(effectiveDate)) return;
    setLoading(true);
    const { radio } = form.getFieldsValue(true);

    const promises = !radio
      ? [
          apiV2EnergyAnalysisUnitConfGroupSavePost({
            unitId,
            effectiveDate: effectiveDate.format('YYYY-MM-DD'),
          }).onError(error => {
            setLoading(false);
            throw error;
          }),
        ]
      : [
          apiV2EnergyUnitBoardRecomputePost({
            energyUnitIds: energyUnitIds,
            startDate: effectiveDate.format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
          }).onError(error => {
            setLoading(false);
            throw error;
          }),
          apiV2EnergyAnalysisUnitConfGroupSavePost({
            unitId,
            effectiveDate: effectiveDate.format('YYYY-MM-DD'),
          }).onError(error => {
            setLoading(false);
            throw error;
          }),
        ];

    Promise.all(promises)
      .then(res => {
        Modal.success({
          content: '保存成功',
          onOk: () => {
            setUnitConfGroupDetailActiveTab('1');
            refresh();
          },
        });
        setEffectiveDate(dayjs());
        setEnergyUnitIds([]);
      })
      .catch(err => {
        Modal.error({
          content: '保存失败',
        });
      })
      .finally(() => {
        setLoading(false);
        form.setFieldsValue({ effectiveDate: dayjs() });
        setEffectiveDate(dayjs());
        closeModal();
        setCurrent(0);
      });
  };

  const next = () => {
    if (current === 1) {
      if (effectiveDate.isAfter(dayjs(), 'day')) {
        setCurrent(current + 1);
      } else {
        if (isNil(energyUnitIds) || energyUnitIds.length === 0) {
          Modal.warning({ content: '请选择用能单元' });
          return;
        } else {
          setCurrent(current + 1);
        }
      }
    } else {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    form.setFieldsValue({ effectiveDate });
    setCurrent(current - 1);
  };

  return (
    <Modal
      title="设置生效时间"
      open={true}
      onCancel={() => {
        closeModal();
        form.setFieldsValue({ effectiveDate: dayjs() });
        setCurrent(0);
        setEffectiveDate(dayjs());
      }}
      size="large"
      destroyOnClose
      footer={
        <>
          {current === StepsType.First && (
            <Space size={8}>
              <Button
                onClick={() => {
                  setEffectiveDate(dayjs());
                  form.setFieldsValue({ effectiveDate: dayjs() });
                  closeModal();
                  setCurrent(0);
                }}
              >
                取消
              </Button>
              <Button type="primary" onClick={next}>
                下一步
              </Button>
            </Space>
          )}
          {effectiveDate?.isAfter(dayjs(), 'day') ? (
            <>
              {current === StepsType.Second && (
                <Space>
                  <Button type="primary" onClick={onFinish}>
                    完成
                  </Button>
                </Space>
              )}
            </>
          ) : (
            <>
              {current === StepsType.Second && (
                <Space size={8}>
                  <Button onClick={prev}>上一步</Button>
                  <Button type="primary" onClick={next}>
                    下一步
                  </Button>
                </Space>
              )}
              {current === StepsType.Third && (
                <Space>
                  <Button loading={loading} type="primary" onClick={onFinish}>
                    确认保存
                  </Button>
                </Space>
              )}
            </>
          )}
        </>
      }
    >
      <Form
        // preserve={false}
        form={form}
        labelAlign="left"
        onFinish={onFinish}
        initialValues={{ effectiveDate: dayjs(), radio: 0 }}
      >
        <div className={styles.steps}>
          <Steps
            current={current}
            items={[
              {
                title: '选择时间',
              },
              {
                title: '选择用能单元',
              },
              {
                title: '完成',
              },
            ]}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.firstStep}>
            {current === StepsType.First && (
              <Form.Item
                rules={[{ required: true }]}
                name="effectiveDate"
                label="生效日期"
                extra={changedUnitConfGroupListDate ? `保存后会覆盖更改用能单元未来配置` : ''}
              >
                <DatePicker
                  style={{ width: '75%' }}
                  onChange={(value, values) => {
                    setEffectiveDate(value);
                  }}
                />
              </Form.Item>
            )}
            {current === StepsType.Second && (
              <>
                <Form.Item
                  rules={[{ required: true, message: '请选择用能单元' }]}
                  name="energyUnitIds"
                  label="用能单元"
                >
                  <TreeSelect
                    popupClassName={styles.treeSelect}
                    treeData={treeData}
                    checkAllAble={true}
                    maxTagCount={'responsive'}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeCheckable
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                    treeCheckStrictly={true}
                    onTreeExpand={handleExpand}
                    treeExpandedKeys={expandedKeys}
                    treeNodeLabelProp="name"
                    placeholder="请选择"
                  />
                </Form.Item>
                <Form.Item rules={[{ required: true }]} name="radio" label="是否重跑数据">
                  <Radio.Group>
                    <Radio value={1}>是</Radio>
                    <Radio value={0}>否</Radio>
                  </Radio.Group>
                </Form.Item>
                <ExclamationCircleOutlined style={{ color: 'var(--mx-warning-color)', fontSize: 12 }} />
                <span className={styles.txt}>数据重计算需要5—30分钟，若相关页面数据未更新可联系系统管理员排查。</span>
              </>
            )}
            {current === StepsType.Third && (
              <div className={styles.finish}>
                <img src={check} alt="" style={{ width: 56, height: 56 }} />
                应用完成
              </div>
            )}
          </div>
        </div>
      </Form>
    </Modal>
  );
}
